
import { useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format, subDays, subMonths, startOfDay, endOfDay, parseISO } from "date-fns";

interface ActivityDataPoint {
  date: string;
  likes: number;
  earnings: number;
}

interface RecipeActivityChartProps {
  data: ActivityDataPoint[];
}

type TimeRange = '7d' | '30d' | '90d' | '1y';

export const RecipeActivityChart = ({ data }: RecipeActivityChartProps) => {
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');

  const getDateRange = (range: TimeRange) => {
    const now = new Date();
    switch (range) {
      case '7d':
        return subDays(now, 7);
      case '30d':
        return subDays(now, 30);
      case '90d':
        return subDays(now, 90);
      case '1y':
        return subMonths(now, 12);
      default:
        return subDays(now, 30);
    }
  };

  // First sort the data by date
  const sortedData = [...data].sort((a, b) => 
    parseISO(a.date).getTime() - parseISO(b.date).getTime()
  );

  // Then filter based on the selected time range
  const rangeStart = startOfDay(getDateRange(timeRange));
  const rangeEnd = endOfDay(new Date());
  
  const filteredData = sortedData.filter(item => {
    const itemDate = parseISO(item.date);
    return itemDate >= rangeStart && itemDate <= rangeEnd;
  });

  console.log('Time range:', timeRange);
  console.log('Range start:', rangeStart);
  console.log('Range end:', rangeEnd);
  console.log('Filtered data length:', filteredData.length);

  const formatDate = (dateStr: string) => {
    const date = parseISO(dateStr);
    return format(date, 'MMM d');
  };

  // If there's no data, show a message
  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recipe Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <p className="text-muted-foreground">No activity data available yet</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle>Recipe Activity</CardTitle>
        <Select
          value={timeRange}
          onValueChange={(value: TimeRange) => setTimeRange(value)}
        >
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Select range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="90d">Last 90 days</SelectItem>
            <SelectItem value="1y">Last year</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart 
              data={filteredData}
              margin={{ top: 10, right: 30, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatDate}
                tick={{ fontSize: 12 }}
                padding={{ left: 20, right: 20 }}
                tickMargin={10}
              />
              <YAxis 
                yAxisId="left"
                tickMargin={10}
              />
              <YAxis 
                yAxisId="right" 
                orientation="right"
                tickMargin={10}
              />
              <Tooltip
                labelFormatter={value => format(parseISO(value), 'MMM d, yyyy')}
                formatter={(value: number, name: string) => {
                  if (name === "earnings") return [`$${value}`, "Earnings"];
                  return [value, name];
                }}
              />
              <Legend wrapperStyle={{ paddingTop: '10px' }} />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="likes"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={false}
                name="Likes"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="earnings"
                stroke="#10b981"
                strokeWidth={2}
                dot={false}
                name="Earnings ($)"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
